import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import uniq from 'lodash/uniq';
import { themeColors } from '../../lib/Color';
import { clearTags, setSearchSource } from '../../actions/search';
import { setColors, clearColors } from '../../actions/ui';
import { EMPTY_ARRAY, RESPONSIVE_SIZE, SEARCH_SOURCE } from '../../constants';
import Button from '../Button';
import Link from '../Link';
import SceneCardLayout from '../SceneCardLayout';
import Section from '../Section';
import WorkingSpinner from '../Button/WorkingSpinner';
import { H4, L2, P1 } from '../Typography';
import Style from '../Style';

const HOVER_TIMEOUT = 500;

class SceneSearchResults extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    allClips: PropTypes.arrayOf(PropTypes.object),
    matchingClips: PropTypes.arrayOf(PropTypes.object),
    backupClips: PropTypes.arrayOf(PropTypes.object),
    backupTitle: PropTypes.string,
    fetching: PropTypes.bool,
    loadMore: PropTypes.func,
    found: PropTypes.number,
    loaded: PropTypes.number,
    showClipCount: PropTypes.bool,
    searchResultsMessage: PropTypes.string,
    isFiltered: PropTypes.bool,
    colors: PropTypes.object,
    updateColors: PropTypes.func,
    clearColors: PropTypes.func,
    clearFilters: PropTypes.func,
    searchSource: PropTypes.string,
    setSearchSource: PropTypes.func,
  };
  static defaultProps = {
    allClips: EMPTY_ARRAY,
    matchingClips: EMPTY_ARRAY,
    backupClips: EMPTY_ARRAY,
    fetching: false,
    loadMore: () => {},
    found: 0,
    loaded: 0,
    showClipCount: true,
    searchResultsMessage: '',
  };

  componentWillMount() {
    this.props.clearColors();
  }

  componentWillUnmount() {
    clearTimeout(this.clearColorsTimeout);
  }

  onSceneHoverToggled = (hovering, sceneId) => {
    if (hovering) {
      clearTimeout(this.clearColorsTimeout);
      const clip = find(this.props.allClips, item => item.scene_id === sceneId);
      this.props.updateColors(themeColors(clip.scene_colors));
    } else {
      this.clearColorsTimeout = setTimeout(
        this.props.clearColors,
        HOVER_TIMEOUT,
      );
    }
  };

  render() {
    const {
      responsive,
      allClips,
      matchingClips,
      backupClips,
      backupTitle,
      fetching,
      loadMore,
      found,
      loaded,
      showClipCount,
      searchResultsMessage,
      isFiltered,
      colors,
      updateColors,
      clearColors,
      clearFilters,
      searchSource,
      setSearchSource,
      ...otherProps
    } = this.props;

    const matchingByScene = groupBy(matchingClips, 'scene_id');
    const allByScene = groupBy(allClips, 'scene_id');
    // Not using Object.keys(matchingByScene) because we want the order from matchingClips.
    const sceneIds = uniq(matchingClips.map(clip => clip.scene_id));
    const scenes = sceneIds.map(sceneId => ({
      allClips: isFiltered ? allByScene[sceneId] : matchingByScene[sceneId],
      matchingClips: isFiltered ? matchingByScene[sceneId] : undefined,
    }));

    // Backup list of results.
    const backupByScene = groupBy(backupClips, 'scene_id');
    const backupSceneIds = uniq(backupClips.map(clip => clip.scene_id)).slice(
      0,
      8,
    );
    const backupScenes = backupSceneIds.map(sceneId => ({
      allClips: backupByScene[sceneId],
      matchingClips: undefined,
    }));

    let outerPadding = '1rem';
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      outerPadding = '7vw';
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      outerPadding = '4rem';
    }

    let showMoreVisible = false;
    let viewAllVisible = false;
    let moreAvailable = found > loaded;
    if (moreAvailable) {
      showMoreVisible = true;
    } else if (searchSource !== SEARCH_SOURCE.ALL_SCENES && !isFiltered) {
      viewAllVisible = true;
    }

    return (
      <div
        style={{
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: colors.light,
        }}
      >
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            minHeight: '100vh',
            padding: `2rem ${outerPadding} 10rem`,
          }}
        >
          {found < 1 &&
            fetching && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '25vh 0',
                }}
              >
                <div style={{ paddingBottom: '1rem' }}>
                  <WorkingSpinner active={true} />
                </div>
                <P1>Loading...</P1>
              </div>
            )}
          {found > 0 && (
            <div>
              <div style={{ opacity: isFiltered || fetching ? 1 : 0 }}>
                <Results title="Results:" message={searchResultsMessage} />
              </div>
              <SceneCardLayout
                scenes={scenes}
                onHoverToggle={this.onSceneHoverToggled}
              />
              <div
                style={{
                  display: showMoreVisible || viewAllVisible ? 'flex' : 'none',
                  justifyContent: 'center',
                  paddingTop: '5rem',
                  marginBottom: '-5rem',
                }}
              >
                {showMoreVisible && (
                  <Button
                    working={fetching}
                    baseColor={colors.dark}
                    size="large"
                    onClick={loadMore}
                  >
                    Load More
                  </Button>
                )}

                {viewAllVisible && (
                  <Button
                    working={fetching}
                    baseColor={colors.dark}
                    size="large"
                    onClick={() => setSearchSource(SEARCH_SOURCE.ALL_SCENES)}
                  >
                    View all scenes
                  </Button>
                )}
              </div>
            </div>
          )}
          {found < 1 &&
            !fetching && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '64rem',
                    textAlign: 'center',
                    margin: 'auto',
                    padding: '9.5rem 2rem 7rem',
                  }}
                >
                  <H4 style={{ fontWeight: 600, color: Style.Colors.shadow2 }}>
                    Hmm… we can’t seem to find something exactly like that in
                    our&nbsp;library.
                  </H4>
                  <P1
                    style={{
                      color: Style.Colors.shadow2,
                      margin: '1rem 0 3.2rem',
                    }}
                  >
                    Try removing some filters or adjusting your search. We’re
                    always adding new clips (over 500 each month!) so there’s
                    always something else to&nbsp;discover!
                  </P1>
                  <div>
                    <Button onClick={this.props.clearFilters} size="large">
                      Clear Filters
                    </Button>
                  </div>
                </div>
                <Results title="Start Browsing:" message={backupTitle} />
                <SceneCardLayout
                  scenes={backupScenes}
                  onHoverToggle={this.onSceneHoverToggled}
                />
              </div>
            )}
        </div>
        <Section
          style={{
            zIndex: 1,
            position: 'absolute',
            top: '100vh',
            left: 0,
            bottom: '-1rem',
          }}
          topAngle={14}
          backgroundColor={colors.dark}
          contentStyle={{ height: '100%' }}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
    allClips: values(state.clips.clips),
    isFiltered: !!state.search.text || state.search.tags.length > 0,
    colors: state.ui.colors,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    clearColors: () => dispatch(clearColors()),
    updateColors: colors => dispatch(setColors(colors)),
    clearFilters: () => dispatch(clearTags()),
    setSearchSource: source => dispatch(setSearchSource(source)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SceneSearchResults);

function Results({ title, message }) {
  return (
    <span>
      {' '}
      <L2
        style={{
          fontWeight: 'regular',
          fontSize: '2.2rem',
          letterSpacing: -0.15,
          color: '#9B9B9B',
        }}
      >
        {title}
      </L2>{' '}
      <L2
        style={{
          fontSize: '2.2rem',
          letterSpacing: -0.15,
          color: '#4A4A4A',
        }}
      >
        {message}
      </L2>
    </span>
  );
}



// WEBPACK FOOTER //
// ./src/components/SceneSearchResults/SceneSearchResults.js